import React from 'react';
import styled from 'styled-components';
import ComparisonRow from './ComparisonRow';
import useLocale from '../../hooks/useLocale';

/* ─── Field extractors ─── */

function fmtTimeRange(r) {
  if (!r) return '';
  return `${r.start || ''} \u2014 ${r.end || ''}`;
}

function fmtLimbNameAndEnd(limb) {
  if (!limb) return '';
  return limb.end_time ? `${limb.name} (ends ${limb.end_time})` : (limb.name || '');
}

function fmtTamilYogam(daily) {
  const ty = daily.yogas_conditions?.tamil_yogam;
  return ty?.primary_name_ta || ty?.primary_name || ty?.primary || '';
}

const COMPARISON_FIELDS = [
  {
    key: 'sunrise',
    labelKey: 'sunrise',
    extract: (p) => p.sun_moon?.sunrise || '',
    deltaKey: 'sunrise_delta',
    matchKey: null,
  },
  {
    key: 'sunset',
    labelKey: 'sunset',
    extract: (p) => p.sun_moon?.sunset || '',
    deltaKey: 'sunset_delta',
    matchKey: null,
  },
  {
    key: 'tithi',
    labelKey: 'tithi',
    extract: (p) => fmtLimbNameAndEnd(p.five_limbs?.tithi),
    deltaKey: 'tithi_end_delta',
    matchKey: 'tithi_match',
  },
  {
    key: 'nakshatra',
    labelKey: 'nakshatra',
    extract: (p) => fmtLimbNameAndEnd(p.five_limbs?.nakshatra),
    deltaKey: 'nakshatra_end_delta',
    matchKey: 'nakshatra_match',
  },
  {
    key: 'yoga',
    labelKey: 'yoga',
    extract: (p) => fmtLimbNameAndEnd(p.five_limbs?.yoga),
    deltaKey: 'yoga_end_delta',
    matchKey: 'yoga_match',
  },
  {
    key: 'karana',
    labelKey: 'karana',
    extract: (p) => fmtLimbNameAndEnd(p.five_limbs?.karana),
    deltaKey: 'karana_end_delta',
    matchKey: 'karana_match',
  },
  {
    key: 'tamil_yogam',
    labelKey: 'tamil_yogam',
    extract: fmtTamilYogam,
    deltaKey: null,
    matchKey: 'tamil_yogam_match',
  },
  {
    key: 'rahu_kaal',
    labelKey: 'rahu_kaal',
    extract: (p) => fmtTimeRange(p.inauspicious?.rahu_kaal),
    deltaKey: 'rahu_kaal_start_delta',
    matchKey: null,
  },
];

/* ─── Styled ─── */

const DesktopTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: ${p => p.theme.spacing[4]}px 0;
  background: ${p => p.theme.colors.surface};
  border-radius: ${p => p.theme.radius.md}px;
  overflow: hidden;
  box-shadow: ${p => p.theme.shadow.card};

  thead th {
    background: ${p => p.theme.colors.background};
    padding: ${p => p.theme.spacing[3]}px;
    text-align: left;
    font-size: ${p => p.theme.typography.sizes.sm};
    color: ${p => p.theme.colors.text.secondary};
    font-weight: ${p => p.theme.typography.weights.semibold};
    border-bottom: 2px solid ${p => p.theme.colors.border};
  }

  @media (max-width: ${p => p.theme.breakpoints.desktop}px) {
    display: none;
  }
`;

const MobileList = styled.dl`
  display: none;

  @media (max-width: ${p => p.theme.breakpoints.desktop}px) {
    display: block;
    margin: ${p => p.theme.spacing[4]}px 0;
  }
`;

/* ─── Component ─── */

const ComparisonTable = ({ result, lang = 'en' }) => {
  const t = useLocale(lang, 'comparison');

  if (!result || !result.drik || !result.vakyam || !result.deltas) return null;

  const { drik, vakyam, deltas } = result;

  const rows = COMPARISON_FIELDS.map(field => {
    const drikValue = field.extract(drik);
    const vakyamValue = field.extract(vakyam);
    const delta = field.deltaKey ? (deltas[field.deltaKey] || null) : null;
    const nameMatch = field.matchKey ? (deltas[field.matchKey] ?? null) : null;
    const label = t.fields?.[field.labelKey] || field.key;
    return { key: field.key, label, drikValue, vakyamValue, delta, nameMatch };
  });

  return (
    <>
      <DesktopTable>
        <thead>
          <tr>
            <th scope="col">{t.fields?.sunrise ? '' : 'Field'}</th>
            <th scope="col">{t.header?.drik || 'Drik'}</th>
            <th scope="col">{t.header?.vakyam || 'Vakyam'}</th>
            <th scope="col">{t.header?.delta || 'Delta'}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <ComparisonRow
              key={r.key}
              label={r.label}
              drikValue={r.drikValue}
              vakyamValue={r.vakyamValue}
              delta={r.delta}
              nameMatch={r.nameMatch}
              lang={lang}
              variant="desktop"
            />
          ))}
        </tbody>
      </DesktopTable>

      <MobileList>
        {rows.map(r => (
          <ComparisonRow
            key={r.key}
            label={r.label}
            drikValue={r.drikValue}
            vakyamValue={r.vakyamValue}
            delta={r.delta}
            nameMatch={r.nameMatch}
            lang={lang}
            variant="mobile"
          />
        ))}
      </MobileList>
    </>
  );
};

export default ComparisonTable;
